import { openDB, DBSchema, IDBPDatabase } from "idb";

interface OfflineOpsDB extends DBSchema {
  mutations: {
    key: string;
    value: {
      id: string;
      table: string;
      operation: "insert" | "update" | "delete" | "upsert";
      payload: Record<string, unknown>;
      createdAt: string;
      retries: number;
    };
    indexes: { "by-created": string };
  };
  cache: {
    key: string;
    value: {
      key: string;
      data: unknown;
      cachedAt: string;
      ttlMs: number;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<OfflineOpsDB>> | null = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB<OfflineOpsDB>("remodel-ops-offline", 1, {
      upgrade(db) {
        const mutStore = db.createObjectStore("mutations", { keyPath: "id" });
        mutStore.createIndex("by-created", "createdAt");
        db.createObjectStore("cache", { keyPath: "key" });
      },
    });
  }
  return dbPromise;
}

// --- Mutation Queue ---

export async function enqueueMutation(
  table: string,
  operation: "insert" | "update" | "delete" | "upsert",
  payload: Record<string, unknown>
) {
  const db = await getDB();
  const id = crypto.randomUUID();
  await db.put("mutations", {
    id,
    table,
    operation,
    payload,
    createdAt: new Date().toISOString(),
    retries: 0,
  });
  return id;
}

export async function getPendingMutations() {
  const db = await getDB();
  return db.getAllFromIndex("mutations", "by-created");
}

export async function removeMutation(id: string) {
  const db = await getDB();
  await db.delete("mutations", id);
}

export async function incrementRetry(id: string) {
  const db = await getDB();
  const mutation = await db.get("mutations", id);
  if (mutation) {
    mutation.retries += 1;
    await db.put("mutations", mutation);
  }
}

export async function getPendingCount(): Promise<number> {
  const db = await getDB();
  return db.count("mutations");
}

// --- Data Cache ---

export async function cacheData(key: string, data: unknown, ttlMs = 1000 * 60 * 60) {
  const db = await getDB();
  await db.put("cache", {
    key,
    data,
    cachedAt: new Date().toISOString(),
    ttlMs,
  });
}

export async function getCachedData<T = unknown>(key: string): Promise<T | null> {
  const db = await getDB();
  const entry = await db.get("cache", key);
  if (!entry) return null;

  const age = Date.now() - new Date(entry.cachedAt).getTime();
  if (age > entry.ttlMs) {
    await db.delete("cache", key);
    return null;
  }
  return entry.data as T;
}

export async function clearCache() {
  const db = await getDB();
  await db.clear("cache");
}
